import { useState, useRef, useEffect } from 'react'
import { supabase } from './supabaseClient'
import { useStoneMatrix } from './hooks/useStoneMatrix'
import { cleanNameForSearch } from './utils/stoneMatrixUtils'
import { buildPersonSearchQuery } from './utils/search'
import { toStoneColumns } from './utils/conditionScoring'
import { fetchStoneCoords } from './utils/mapData'
import { useVisitorTracking } from './hooks/useVisitorTracking'
import { haversineMeters, distanceLabel, openInMaps } from './utils/geo'
import StoneMap from './components/StoneMap'
import SeverityBadge from './components/SeverityBadge'
import { REL_LABEL, INVERSE_REL, CEMETERY_ID, OBC_CEMETERY_ID } from './constants'

// ── HEADER ───────────────────────────────────────────────────
function Header({ onMap, onRecent, onAdmin, onAccount, onHome }) {
  return (
    <div className="bg-gray-800 p-4 flex items-center justify-between">
      <h1 className="text-xl font-bold text-green-400 cursor-pointer" onClick={onHome}>
        Granite Graph
      </h1>
      <div className="flex gap-3">
        <button onClick={onMap} className="text-gray-300 text-sm hover:text-white">Map</button>
        <button onClick={onRecent} className="text-gray-300 text-sm hover:text-white">Recent</button>
        <button onClick={onAccount} className="text-gray-300 text-sm hover:text-white">Account</button>
        {onAdmin && (
          <button onClick={onAdmin} className="text-yellow-400 text-sm hover:text-yellow-300">Admin</button>
        )}
        <button onClick={() => supabase.auth.signOut()} className="text-gray-300 text-sm hover:text-white">Sign Out</button>
      </div>
    </div>
  )
}

// ── MAIN COMPONENT ───────────────────────────────────────────
export default function Home({ session, onMap, onRecent, onAdmin, onAccount }) {
  // Core mode
  const [mode, setMode] = useState('landing') // landing | photograph | search

  // Photograph phases
  const [photoPhase, setPhotoPhase] = useState('capture') // capture | matrix | match | done

  // Image state
  const [image, setImage] = useState(null)
  const [loading, setLoading] = useState(false)
  const imageBase64Ref = useRef(null)

  const {
    stoneMatrix, setStoneMatrix,
    matchingIndex, setMatchingIndex,
    matchSearchQuery, setMatchSearchQuery,
    matchSearchResults, setMatchSearchResults,
    matchSearching, matchSearchAttempted, setMatchSearchAttempted,
    initMatrix, resetMatrix, prepareMatch,
    updatePersonRole, updateCorrectedName, updateRelField,
    searchRelatedPerson,
    confirmRelationship, skipRelationship,
    confirmRelationshipExternal, confirmRelationshipNameOnly, removeConfirmedRelationship,
    handleMatchSearch, selectMatch, advancePerson, skipMatch, markAsNewRecord,
  } = useStoneMatrix()

  const [saving, setSaving] = useState(false)

  // Cemetery context — persists across stones within a session
  const [cemId, setCemId] = useState(CEMETERY_ID)

  // Plot location (capture)
  const [plotNumber, setPlotNumber] = useState('')

  // Plot location (search/edit)
  const [editPlotNumber, setEditPlotNumber] = useState('')
  const [savingPlot, setSavingPlot] = useState(false)

  // Field notes
  const [volunteerNotes, setVolunteerNotes] = useState('')
  const [selectedFlags, setSelectedFlags] = useState([])
  const [showNotes, setShowNotes] = useState(false)
  const [savingNotes, setSavingNotes] = useState(false)
  const [gpsStatus, setGpsStatus] = useState(null)

  // Search mode state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [searchSelected, setSearchSelected] = useState(null)
  const [searchStoneData, setSearchStoneData] = useState(null)
  const [searching, setSearching] = useState(false)
  const { visitorLocation, isTracking, startTracking, stopTracking } = useVisitorTracking()

  useEffect(() => {
    return () => {
      if (gpsWatchIdRef.current !== null) navigator.geolocation.clearWatch(gpsWatchIdRef.current)
    }
  }, [])
  const [pendingPhotoFor, setPendingPhotoFor] = useState(null)

  const fileInput = useRef(null)
  const currentStoneRef = useRef(null)
  const gpsPromiseRef = useRef(null)
  const gpsWatchIdRef = useRef(null)

  // ── IMAGE HANDLING ───────────────────────────────────────
  const handlePhoto = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = () => {
      const b64 = reader.result.split(',')[1]
      imageBase64Ref.current = b64
      setImage(reader.result)
    }
    reader.readAsDataURL(file)
    currentStoneRef.current = null
    resetMatrix()
    setPhotoPhase('capture')
    setPlotNumber('')
    setVolunteerNotes('')
    setSelectedFlags([])
    setShowNotes(false)

    // GPS is *not* started here. Starting it from this file-input onChange —
    // which on iOS fires after returning from the native camera app — is not
    // reliably treated as a user gesture by Safari/WebKit, and watchPosition
    // can silently never deliver a fix as a result. Started instead from the
    // "Analyze with Gemini" tap below, a direct, unambiguous click.
    cancelGpsWatch()
  }

  const resizeImage = (base64) => new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const maxSize = 1024
      let w = img.width, h = img.height
      if (w > h && w > maxSize) { h = (h * maxSize) / w; w = maxSize }
      else if (h > maxSize) { w = (w * maxSize) / h; h = maxSize }
      canvas.width = w; canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      resolve(canvas.toDataURL('image/jpeg', 0.8).split(',')[1])
    }
    img.src = 'data:image/jpeg;base64,' + base64
  })

  // ── GEMINI ANALYSIS ──────────────────────────────────────
  const analyzePhoto = async () => {
    if (!imageBase64Ref.current) return

    // Start GPS acquisition here, synchronously, as a direct response to this
    // button tap — a real user gesture, unlike the file-input onChange that
    // fires after the native camera returns (see handlePhoto). Runs in the
    // background through the Gemini call and matrix review, same as before.
    cancelGpsWatch()
    const gpsPromise = getAccuratePosition()
    gpsPromise.catch(() => {}) // real handling happens when saveStone awaits gpsPromiseRef.current
    gpsPromiseRef.current = gpsPromise

    setLoading(true)
    try {
      const resizedBase64 = await resizeImage(imageBase64Ref.current)
      const geminiResponse = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: resizedBase64 })
      })
      const geminiData = await geminiResponse.json()
      if (!geminiData.candidates || geminiData.candidates.length === 0) {
        throw new Error('Gemini error: ' + (geminiData.error?.message || JSON.stringify(geminiData)))
      }
      const extracted = JSON.parse(geminiData.candidates[0].content.parts[0].text.replace(/```json|```/g, '').trim())
      initMatrix(extracted.people || [], extracted.condition_assessment)
      setPhotoPhase('matrix')
    } catch (err) {
      console.error(err)
      alert('Error analyzing photo: ' + err.message)
    }
    setLoading(false)
  }

  const proceedToMatch = () => {
    if (pendingPhotoFor && stoneMatrix?.people?.[0]?.matchStatus === 'pending') {
      const nextIndex = stoneMatrix.people.length > 1 ? 1 : 0
      const next = stoneMatrix.people[nextIndex]
      setStoneMatrix(prev => ({
        ...prev,
        people: prev.people.map((p, i) =>
          i === 0 ? { ...p, matchedRecord: pendingPhotoFor, matchStatus: 'matched' } : p
        )
      }))
      setMatchingIndex(nextIndex)
      setMatchSearchQuery(cleanNameForSearch(next.correctedName))
      setMatchSearchResults(next.preSearchResults || [])
      setMatchSearchAttempted(!!(next.preSearchResults?.length))
      setPhotoPhase('match')
      return
    }
    prepareMatch()
    setPhotoPhase('match')
  }

  const nextPerson = () => { const done = advancePerson(); if (done) saveStone() }

  // ── GPS ──────────────────────────────────────────────────
  const cancelGpsWatch = () => {
    if (gpsWatchIdRef.current !== null) {
      navigator.geolocation.clearWatch(gpsWatchIdRef.current)
      gpsWatchIdRef.current = null
    }
  }

  const getAccuratePosition = () => new Promise((resolve, reject) => {
    let bestPosition = null
    let resolved = false
    setGpsStatus('Acquiring GPS...')
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        bestPosition = pos
        const acc = Math.round(pos.coords.accuracy)
        setGpsStatus('GPS: ' + acc + 'm accuracy' + (acc <= 10 ? ' ✓' : ' (improving...)'))
        if (pos.coords.accuracy <= 10) {
          navigator.geolocation.clearWatch(watchId)
          gpsWatchIdRef.current = null
          setGpsStatus(null)
          resolved = true
          resolve(pos)
        }
      },
      (err) => {
        if (bestPosition) { gpsWatchIdRef.current = null; setGpsStatus(null); resolved = true; resolve(bestPosition); return }
        if (err.code === 1) {
          navigator.geolocation.clearWatch(watchId)
          gpsWatchIdRef.current = null
          setGpsStatus(null)
          resolved = true
          reject(new Error(
            'Location access denied. To fix this:\n' +
            '• iPhone: Settings → Privacy & Security → Location Services → Safari → While Using App\n' +
            '• Android: tap the lock icon in your browser address bar → Permissions → Location → Allow\n\n' +
            'Then reload the page and try again.'
          ))
          return
        }
        // TIMEOUT / POSITION_UNAVAILABLE with no fix yet — this is routine on a cold GPS start
        // or under tree cover, not fatal. Keep the watch running; the 30s failsafe below decides.
        setGpsStatus('GPS: waiting for signal...')
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    )
    gpsWatchIdRef.current = watchId
    // Take best available fix after 30s rather than making volunteer stand still indefinitely
    setTimeout(() => {
      if (resolved) return
      navigator.geolocation.clearWatch(watchId)
      if (gpsWatchIdRef.current === watchId) gpsWatchIdRef.current = null
      setGpsStatus(null)
      if (bestPosition) {
        resolve(bestPosition)
      } else { reject(new Error('Could not get GPS position')) }
    }, 30000)
  })

  // ── SAVE EVERYTHING ──────────────────────────────────────
  const saveStone = async () => {
    setSaving(true)
    try {
      // 0. Pre-create deceased records for field-discovered persons (matchStatus === 'new')
      let resolvedPeople = [...stoneMatrix.people]
      for (let i = 0; i < resolvedPeople.length; i++) {
        const p = resolvedPeople[i]
        if (p.matchStatus !== 'new') continue
        const parts = (p.correctedName || '').trim().split(/\s+/)
        const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : parts[0]
        const lastName  = parts.length > 1 ? parts[parts.length - 1] : null
        const { data: newRec, error } = await supabase.from('deceased').insert({
          first_name: firstName,
          last_name: lastName,
          maiden_name: p.geminiData.maiden_name || null,
          date_of_birth_verbatim: p.geminiData.date_of_birth_verbatim || null,
          date_of_death_verbatim: p.geminiData.date_of_death_verbatim || null,
          cemetery_id: cemId,
          notes: 'Field-catalogued. Requires curation in Person Research.',
        }).select().single()
        if (!error && newRec) {
          resolvedPeople[i] = { ...p, matchedRecord: newRec }
          await supabase.from('activity_log').insert({
            user_id: session.user.id, action: 'new_record_created', entity_type: 'deceased',
            entity_id: newRec.deceased_id, cemetery_id: cemId,
            metadata: { full_name: p.correctedName, source: 'field_capture', notes: 'stub from field tool' }
          })
        } else console.warn('New record insert failed:', error?.message)
      }

      // 1. Upload photo
      const b64 = imageBase64Ref.current
      const byteString = atob(b64)
      const byteArray = new Uint8Array(byteString.length)
      for (let i = 0; i < byteString.length; i++) byteArray[i] = byteString.charCodeAt(i)
      const blob = new Blob([byteArray], { type: 'image/jpeg' })
      const fileName = Date.now() + '_' + session.user.id + '.jpg'

      const gpsPromise = gpsPromiseRef.current || getAccuratePosition()
      gpsPromiseRef.current = null // consume it now so a retry after a failed save starts a fresh fix, not the same dead promise
      const gpsSettled = gpsPromise.then(pos => ({ ok: true, pos })).catch(err => ({ ok: false, err }))

      // Don't make the volunteer wait on GPS beyond however long the photo
      // upload itself takes — a fix can legitimately take up to 30s (see
      // getAccuratePosition), and a fast review banks little overlap time
      // against that. Race it against the upload instead: if GPS isn't ready
      // by the time the upload finishes, stop waiting and save without it —
      // isolated the same way a GPS failure already is (Decision 5: a stone
      // can be catalogued without a reliable fix). GPS keeps running in the
      // background regardless; if it resolves after we stopped waiting, patch
      // the location in below once the stone row exists.
      const uploadPromise = supabase.storage.from('Stone_Images').upload(fileName, blob, { contentType: 'image/jpeg' })
      const raceResult = await Promise.race([gpsSettled, uploadPromise.then(() => 'upload-first')])
      const uploadResult = await uploadPromise
      if (uploadResult.error) throw uploadResult.error

      const gpsResult = raceResult === 'upload-first' ? null : raceResult
      if (gpsResult && !gpsResult.ok) console.warn('Saving without GPS:', gpsResult.err?.message)

      const position = gpsResult?.ok ? gpsResult.pos : null
      const lat = position?.coords.latitude ?? null
      const lng = position?.coords.longitude ?? null
      const accuracy = position?.coords.accuracy ?? null
      const { data: { publicUrl } } = supabase.storage.from('Stone_Images').getPublicUrl(fileName)

      // 2. Create stone record
      const occupants = resolvedPeople.filter(p => p.role === 'occupant')
      const inscriptionText = resolvedPeople.map(p =>
        [p.correctedName, p.geminiData.date_of_birth_verbatim, p.geminiData.date_of_death_verbatim,
          ...(p.geminiData.kinship_hints || [])].filter(Boolean).join(' ')
      ).join(' | ')

      const { data: stoneData, error: stoneError } = await supabase.from('stones').insert({
        cemetery_id: cemId,
        volunteer_notes: volunteerNotes,
        stone_condition: stoneMatrix.stone_condition,
        condition_notes: stoneMatrix.stone_notes,
        inscription_text: inscriptionText,
        field_status: (selectedFlags.length > 0 || lat == null) ? 'needs_followup' : 'complete',
        flags: selectedFlags,
        location: lat != null && lng != null ? 'SRID=4326;POINT(' + lng + ' ' + lat + ')' : null,
        gps_accuracy_m: accuracy,
        plot_number: plotNumber || null,
        ...toStoneColumns(stoneMatrix.conditionAssessment, stoneMatrix.severity)
      }).select().single()
      if (stoneError) throw stoneError

      currentStoneRef.current = { stoneData, lat, lng, accuracy }

      // GPS was still pending when we stopped racing it against the upload —
      // it's still running. If it resolves later (even after this save has
      // finished and the volunteer has moved on), patch the location in
      // quietly rather than leaving it needs_followup for no reason.
      if (gpsResult === null) {
        const stoneId = stoneData.stone_id
        gpsSettled.then(outcome => {
          if (!outcome.ok) return
          const p = outcome.pos
          supabase.from('stones').update({
            location: 'SRID=4326;POINT(' + p.coords.longitude + ' ' + p.coords.latitude + ')',
            gps_accuracy_m: p.coords.accuracy,
          }).eq('stone_id', stoneId)
            .then(({ error }) => { if (error) console.warn('Late GPS patch failed:', error.message) })
        })
      }

      // 3. Save photo
      await supabase.from('stone_photos').insert({
        stone_id: stoneData.stone_id, photo_url: publicUrl,
        side: 'front', taken_by: session.user.id, is_primary: true
      })

      // 4. Link matched people to stone
      for (const person of resolvedPeople) {
        if (person.matchedRecord) {
          await supabase.from('stone_deceased').insert({
            stone_id: stoneData.stone_id,
            deceased_id: person.matchedRecord.deceased_id,
            confirmed_by: session.user.id,
            confirmed_at: new Date().toISOString(),
            match_method: 'volunteer_confirmed',
            role: person.role
          })

          // Update maiden name if Gemini found one and record has none
          if (person.geminiData.maiden_name && !person.matchedRecord.maiden_name) {
            await supabase.from('deceased').update({ maiden_name: person.geminiData.maiden_name })
              .eq('deceased_id', person.matchedRecord.deceased_id)
            await supabase.from('activity_log').insert({
              user_id: session.user.id, action: 'maiden_name_added', entity_type: 'deceased',
              entity_id: person.matchedRecord.deceased_id, cemetery_id: cemId,
              metadata: { maiden_name: person.geminiData.maiden_name, source: 'gemini_stone_ocr', stone_id: stoneData.stone_id }
            })
          }

          // Log activity
          await supabase.from('activity_log').insert({
            user_id: session.user.id, action: 'match_confirmed', entity_type: 'stone_deceased',
            entity_id: stoneData.stone_id, cemetery_id: cemId,
            metadata: { deceased_name: person.matchedRecord.full_name, role: person.role, gps: { lat, lng, accuracy } }
          })
        }
      }

      // 5. Save confirmed kinship relationships
      const sid = stoneData.stone_id
      const saveKinshipPair = async (aId, bId, typeAtoB, hint, confidence = 'probable') => {
        const typeBtoA = INVERSE_REL[typeAtoB] || 'unknown'
        await Promise.all([
          supabase.from('kinship').upsert(
            { primary_deceased_id: aId, relative_deceased_id: bId, relationship_type: typeAtoB, source: 'stone_inscription', confidence, notes: hint, stone_id: sid },
            { onConflict: 'primary_deceased_id,relative_deceased_id,relationship_type', ignoreDuplicates: true }
          ),
          supabase.from('kinship').upsert(
            { primary_deceased_id: bId, relative_deceased_id: aId, relationship_type: typeBtoA, source: 'stone_inscription', confidence, notes: hint, stone_id: sid },
            { onConflict: 'primary_deceased_id,relative_deceased_id,relationship_type', ignoreDuplicates: true }
          ),
        ])
      }

      // Helper: create a kin-reference stub for an unresolved name and return its deceased_id
      const createKinStub = async (rawName, contextNote) => {
        const parts = rawName.trim().split(/\s+/)
        const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : parts[0]
        const lastName  = parts.length > 1 ? parts[parts.length - 1] : null
        const { data: stub, error } = await supabase.from('deceased').insert({
          first_name: firstName,
          last_name: lastName,
          cemetery_id: cemId,
          notes: contextNote,
        }).select('deceased_id').single()
        if (error) { console.warn('Kin stub insert failed:', error.message); return null }
        return stub.deceased_id
      }

      for (const person of resolvedPeople) {
        if (!person.matchedRecord) continue
        const personId = person.matchedRecord.deceased_id
        const personLabel = person.correctedName || person.matchedRecord.full_name || 'unknown'

        for (const rel of person.confirmedRelationships) {
          if (rel.objectDeceasedId) {
            await saveKinshipPair(personId, rel.objectDeceasedId, rel.type, rel.hint)
          } else if (rel.objectIndex != null) {
            const objectPerson = resolvedPeople[rel.objectIndex]
            if (!objectPerson?.matchedRecord) continue
            await saveKinshipPair(personId, objectPerson.matchedRecord.deceased_id, rel.type, rel.hint)
          } else if (rel.objectName && rel.objectName !== 'Unknown') {
            const stubId = await createKinStub(
              rel.objectName,
              `Kin reference: ${rel.type} of ${personLabel}. Named on stone inscription (field capture, stone ${sid}).`
            )
            if (stubId) await saveKinshipPair(personId, stubId, rel.type, rel.hint, 'possible')
          }
        }

        // Auto-save unconfirmed same-stone relationships by name matching
        for (const rel of person.relationships) {
          const rawName = (rel.relatedName || rel.rawNames?.[0] || '').trim().toLowerCase()
          if (!rawName) continue
          const sameStoneMatch = resolvedPeople.find(op =>
            op !== person && op.matchedRecord &&
            (op.correctedName || '').toLowerCase().split(/\s+/).some(w => w.length > 2 && rawName.includes(w))
          )
          if (sameStoneMatch) {
            await saveKinshipPair(personId, sameStoneMatch.matchedRecord.deceased_id, rel.type, rel.hint)
          } else {
            const displayName = rel.relatedName || rel.rawNames?.[0] || ''
            if (!displayName) continue
            const stubId = await createKinStub(
              displayName,
              `Kin reference: ${rel.type} of ${personLabel}. Named on stone inscription (field capture, stone ${sid}). Needs follow-up — not in local database at time of capture.`
            )
            if (stubId) await saveKinshipPair(personId, stubId, rel.type, rel.hint, 'possible')
          }
        }
      }

      alert(
        'Stone saved! ' + occupants.length + ' occupant(s) cataloged.' +
        (lat == null ? '\n\n⚠ No GPS fix — saved without a location. Flagged for follow-up; it can be re-surveyed later.' : '')
      )
      setPhotoPhase('done')
    } catch (err) {
      console.error(err)
      alert('Error saving stone: ' + err.message)
    }
    setSaving(false)
  }

  const saveNotes = async () => {
    if (!currentStoneRef.current) return
    setSavingNotes(true)
    try {
      const { stoneData } = currentStoneRef.current
      await supabase.from('stones').update({
        volunteer_notes: volunteerNotes,
        flags: selectedFlags,
        field_status: selectedFlags.length > 0 ? 'needs_followup' : 'complete'
      }).eq('stone_id', stoneData.stone_id)
      alert('Notes saved!')
      setShowNotes(false)
    } catch (err) { alert('Error: ' + err.message) }
    setSavingNotes(false)
  }

  const clearAndReset = () => {
    cancelGpsWatch()
    gpsPromiseRef.current = null
    setImage(null); imageBase64Ref.current = null
    setStoneMatrix(null); setPhotoPhase('capture')
    currentStoneRef.current = null
    setVolunteerNotes(''); setSelectedFlags([]); setShowNotes(false)
    setGpsStatus(null); setMatchingIndex(0)
    setMatchSearchQuery(''); setMatchSearchResults([])
    setPendingPhotoFor(null)
    setMode('landing')
  }

  // ── SEARCH FUNCTIONS ─────────────────────────────────────
  const handleVolunteerSearch = async (overrideQuery) => {
    const q = overrideQuery || searchQuery
    if (!q.trim()) return
    setSearching(true); setSearchResults(null); setSearchSelected(null); setSearchStoneData(null)
    const { data, error } = await buildPersonSearchQuery(q, null, 50)
    if (error) { alert('Search error: ' + error.message) } else { setSearchResults(data || []) }
    setSearching(false)
  }

  const selectSearchRecord = async (record) => {
    setSearchSelected(record); setSearchStoneData(null); setEditPlotNumber('')
    if (record.is_occupant) {
      const { data, error } = await supabase.from('stone_deceased')
        .select('stones ( stone_id, gps_accuracy_m, condition_notes, inscription_text, plot_number, stone_photos ( photo_url, is_primary ) )')
        .eq('deceased_id', record.deceased_id).eq('role', 'occupant')
      if (!error && data?.length > 0) {
        // Prefer the stone that has photos (imported stone + photographed stone may both exist)
        const best = data.find(d => d.stones?.stone_photos?.length > 0) || data[0]
        if (best?.stones) {
          const stoneCoord = await fetchStoneCoords(best.stones.stone_id)
          const stoneData = { ...best.stones, lat: stoneCoord?.lat, lng: stoneCoord?.lng }
          setSearchStoneData(stoneData)
          setEditPlotNumber(stoneData.plot_number || '')
        }
      }
    }
  }

  const savePlotNumber = async () => {
    if (!searchStoneData?.stone_id) return
    setSavingPlot(true)
    const { data: { session } } = await supabase.auth.getSession()
    const resp = await fetch('/api/update-plot-number', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
      body: JSON.stringify({ stone_id: searchStoneData.stone_id, plot_number: editPlotNumber || null }),
    })
    if (!resp.ok) {
      const { error } = await resp.json().catch(() => ({}))
      alert('Could not save plot number: ' + (error || resp.statusText))
      setSavingPlot(false)
      return
    }
    const { plot_number } = await resp.json()
    setSearchStoneData(prev => ({ ...prev, plot_number }))
    setEditPlotNumber(plot_number || '')
    setSavingPlot(false)
  }

  // ── LANDING ──────────────────────────────────────────────
  if (mode === 'landing') {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Header onMap={onMap} onRecent={onRecent} onAdmin={onAdmin} onAccount={onAccount} onHome={() => { clearAndReset(); setMode('landing') }} />
        <div className="p-6 max-w-lg mx-auto">
          <p className="text-gray-300 text-center mb-6 mt-4">What would you like to do?</p>

          {/* Cemetery selector — persists across stones in a session */}
          <div className="mb-6">
            <p className="text-gray-500 text-xs text-center uppercase tracking-widest mb-2">Cemetery</p>
            <div className="flex rounded-lg overflow-hidden border border-gray-600">
              <button onClick={() => setCemId(CEMETERY_ID)}
                className={`flex-1 py-3 text-sm font-semibold transition-colors ${cemId === CEMETERY_ID ? 'bg-green-700 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                Seaview Cemetery
              </button>
              <button onClick={() => setCemId(OBC_CEMETERY_ID)}
                className={`flex-1 py-3 text-sm font-semibold transition-colors ${cemId === OBC_CEMETERY_ID ? 'bg-green-700 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                Old Baptist Cemetery
              </button>
            </div>
          </div>

          <input type="file" accept="image/*" capture="environment" ref={fileInput}
            onChange={(e) => { handlePhoto(e); setMode('photograph') }} className="hidden" />
          <button onClick={() => fileInput.current.click()}
            className="w-full bg-green-700 hover:bg-green-600 text-white font-bold py-8 rounded-lg text-xl mb-4">
            📷 Photograph Stone
          </button>
          <button onClick={() => setMode('search')}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-8 rounded-lg text-xl">
            🔍 Search Records
          </button>
        </div>
      </div>
    )
  }

  // ── SEARCH ───────────────────────────────────────────────
  if (mode === 'search') {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Header onMap={onMap} onRecent={onRecent} onAdmin={onAdmin} onAccount={onAccount} onHome={() => { clearAndReset(); setMode('landing') }} />
        <div className="p-4 max-w-lg mx-auto">
          <button onClick={() => { setMode('landing'); setSearchResults(null); setSearchSelected(null); setSearchQuery('') }}
            className="text-gray-300 text-sm hover:text-white mb-4">← Back</button>

          {!searchSelected && (
            <>
              <div className="flex gap-2 mb-4">
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleVolunteerSearch()}
                  placeholder="Last name, First name, or First Last"
                  className="flex-1 bg-white border-2 border-green-500 rounded-lg p-3 text-gray-900 placeholder-gray-500 outline-none focus:ring-2 focus:ring-green-400 font-medium"
                  autoFocus />
                <button onClick={() => handleVolunteerSearch()} disabled={searching}
                  className="bg-green-700 hover:bg-green-600 text-white font-bold px-4 rounded-lg">
                  {searching ? '...' : 'Search'}
                </button>
              </div>
              {searchResults && (
                <div>
                  <p className="text-green-400 font-bold mb-3">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found</p>
                  {searchResults.length === 0 && (
                    <div className="bg-gray-800 rounded-lg p-4"><p className="text-gray-300">No records found.</p></div>
                  )}
                  {searchResults.map(record => (
                    <div key={record.deceased_id} onClick={() => selectSearchRecord(record)}
                      className="bg-gray-800 rounded-lg p-4 mb-2 cursor-pointer hover:bg-gray-700 border border-gray-600">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-white">{record.full_name}</p>
                          {record.maiden_name && <p className="text-gray-300 text-sm">nee {record.maiden_name}</p>}
                          <div className="flex gap-3 mt-1">
                            {record.date_of_birth_verbatim && <p className="text-gray-300 text-xs">b. {record.date_of_birth_verbatim}</p>}
                            {record.date_of_death_verbatim && <p className="text-gray-300 text-xs">d. {record.date_of_death_verbatim}</p>}
                          </div>
                        </div>
                        <span className={record.is_photographed ? 'text-green-400 text-xs' : record.is_occupant ? 'text-blue-400 text-xs' : 'text-gray-400 text-xs'}>
                          {record.is_photographed ? 'Photographed' : record.is_occupant ? 'Cataloged' : 'Not yet cataloged'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {searchSelected && (
            <div>
              <button onClick={() => { setSearchSelected(null); setSearchStoneData(null) }}
                className="text-gray-300 text-sm hover:text-white mb-4">← Back to results</button>
              {searchStoneData?.stone_photos?.length > 0 && (
                <img src={(searchStoneData.stone_photos.find(p => p.is_primary) || searchStoneData.stone_photos[0]).photo_url}
                  alt="Gravestone" className="w-full rounded-lg mb-4" />
              )}
              {searchStoneData && (!searchStoneData.stone_photos || searchStoneData.stone_photos.length === 0) && (
                <div className="bg-gray-800 border border-gray-600 rounded-lg p-3 mb-4">
                  <p className="text-gray-400 text-sm">Stone cataloged — field photo not yet on file.</p>
                </div>
              )}
              <div className="bg-gray-800 rounded-lg p-4 mb-4">
                <h2 className="text-xl font-bold text-white mb-1">{searchSelected.full_name}</h2>
                {searchSelected.maiden_name && <p className="text-gray-300 text-sm mb-2">nee {searchSelected.maiden_name}</p>}
                <div className="flex gap-4">
                  {searchSelected.date_of_birth_verbatim && <div><p className="text-gray-400 text-xs">Born</p><p className="text-white text-sm">{searchSelected.date_of_birth_verbatim}</p></div>}
                  {searchSelected.date_of_death_verbatim && <div><p className="text-gray-400 text-xs">Died</p><p className="text-white text-sm">{searchSelected.date_of_death_verbatim}</p></div>}
                </div>
              </div>
              {searchStoneData && (
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  {searchStoneData.condition_notes && <p className="text-gray-300 text-sm mb-2">{searchStoneData.condition_notes}</p>}
                  {searchStoneData.inscription_text && (
                    <div className="mt-2">
                      <p className="text-gray-400 text-xs mb-1">Inscription</p>
                      <p className="text-white text-sm font-mono">{searchStoneData.inscription_text}</p>
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-gray-600">
                    <p className="text-gray-400 text-xs mb-1">Plot Number</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editPlotNumber}
                        onChange={e => setEditPlotNumber(e.target.value)}
                        placeholder="e.g. 10.1"
                        className="flex-1 bg-white border-2 border-green-500 rounded p-2 text-gray-900 font-bold text-sm outline-none focus:ring-2 focus:ring-green-400 placeholder-gray-400"
                      />
                      <button
                        onClick={savePlotNumber}
                        disabled={savingPlot || editPlotNumber === (searchStoneData.plot_number || '')}
                        className="bg-green-700 hover:bg-green-600 disabled:bg-gray-600 text-white font-bold px-4 rounded text-sm"
                      >
                        {savingPlot ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {searchStoneData?.lat && searchStoneData?.lng && (
                <div className="mb-4">
                  <div className="flex items-baseline gap-3 mb-2">
                    <p className="text-green-400 font-bold">Find this stone</p>
                    {visitorLocation && (
                      <span className="text-white text-sm">
                        {distanceLabel(haversineMeters(visitorLocation.lat, visitorLocation.lng, searchStoneData.lat, searchStoneData.lng))}
                      </span>
                    )}
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <StoneMap
                      stones={[{ stone_id: searchStoneData.stone_id, lat: searchStoneData.lat, lng: searchStoneData.lng }]}
                      visitorLocation={visitorLocation}
                      showRoute
                      height="200px"
                      zoom={19}
                      renderPopup={() => searchSelected.full_name}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openInMaps(searchStoneData.lat, searchStoneData.lng)}
                      className="flex-1 bg-blue-700 hover:bg-blue-600 text-white py-3 rounded-lg font-bold text-sm">
                      Open in Maps
                    </button>
                    {!isTracking ? (
                      <button onClick={startTracking}
                        className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg font-bold text-sm">
                        Show My Location
                      </button>
                    ) : (
                      <button onClick={stopTracking}
                        className="flex-1 bg-green-900 hover:bg-green-800 text-white py-3 rounded-lg font-bold text-sm">
                        {visitorLocation ? '● Tracking' : 'Locating...'}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {!searchSelected.is_occupant && (
                <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                  <p className="text-gray-300 text-sm mb-3">
                    {searchSelected.is_mentioned
                      ? 'This person is mentioned on another stone but has no photographed stone of their own.'
                      : 'This stone has not been photographed yet.'}
                  </p>
                  <label style={{
                    display: 'block', width: '100%', padding: '14px',
                    backgroundColor: '#15803d', color: 'white', fontWeight: 'bold',
                    fontSize: '1rem', borderRadius: '8px', cursor: 'pointer',
                    textAlign: 'center', boxSizing: 'border-box'
                  }}>
                    📷 Photograph this stone now
                    <input type="file" accept="image/*" capture="environment"
                      onChange={(e) => {
                        setPendingPhotoFor(searchSelected)
                        setSearchResults(null); setSearchSelected(null)
                        setSearchQuery(''); setSearchStoneData(null)
                        handlePhoto(e); setMode('photograph')
                      }}
                      style={{ display: 'none' }} />
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── PHOTOGRAPH ───────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      <div className="p-4 max-w-lg mx-auto">

        {/* ── PHASE: CAPTURE ── */}
        {photoPhase === 'capture' && (
          <>
            <button onClick={() => setMode('landing')} className="text-gray-300 text-sm hover:text-white mb-4">← Back</button>

            {gpsStatus && (
              <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-blue-700">
                <p className="text-blue-400 text-sm">📍 {gpsStatus}</p>
              </div>
            )}

            {/* Plot number — entered first to confirm location */}
            <div className="bg-gray-800 rounded-lg p-4 mb-4 border border-gray-600">
              <label className="block text-green-400 font-bold text-sm mb-2">Plot Number</label>
              <input
                type="text"
                value={plotNumber}
                onChange={e => setPlotNumber(e.target.value)}
                placeholder="Enter plot number"
                className="w-full bg-white border-2 border-green-500 rounded p-3 text-gray-900 text-xl font-bold outline-none focus:ring-2 focus:ring-green-400 placeholder-gray-400"
              />
            </div>

            {pendingPhotoFor && !image && (
              <div className="bg-gray-700 rounded-lg p-4 mb-4">
                <p className="text-green-400 font-bold mb-1">📋 Ready to photograph:</p>
                <p className="text-white font-bold text-lg">{pendingPhotoFor.full_name}</p>
                {pendingPhotoFor.date_of_death_verbatim && <p className="text-gray-300 text-sm">d. {pendingPhotoFor.date_of_death_verbatim}</p>}
                <input type="file" accept="image/*" capture="environment"
                  onChange={(e) => { handlePhoto(e) }}
                  style={{
                    display: 'block', width: '100%', padding: '16px',
                    backgroundColor: '#15803d', color: 'white', fontWeight: 'bold',
                    fontSize: '1.125rem', borderRadius: '8px', marginTop: '12px',
                    cursor: 'pointer', border: 'none'
                  }} />
              </div>
            )}

            {image && (
              <div className="mb-4">
                <img src={image} alt="Gravestone" className="w-full rounded-lg mb-3" />
                <button onClick={analyzePhoto} disabled={loading}
                  className="w-full bg-blue-700 hover:bg-blue-600 disabled:bg-blue-900 text-white font-bold py-3 rounded-lg">
                  {loading ? 'Analyzing...' : 'Analyze with Gemini'}
                </button>
              </div>
            )}

            {!image && !pendingPhotoFor && (
              <input type="file" accept="image/*" capture="environment"
                onChange={(e) => { handlePhoto(e) }}
                style={{
                  display: 'block', width: '100%', padding: '16px',
                  backgroundColor: '#15803d', color: 'white', fontWeight: 'bold',
                  fontSize: '1.125rem', borderRadius: '8px',
                  cursor: 'pointer', border: 'none'
                }} />
            )}
          </>
        )}

        {/* ── PHASE: MATRIX ── */}
        {photoPhase === 'matrix' && stoneMatrix && (
          <>
            {gpsStatus && (
              <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-blue-700">
                <p className="text-blue-400 text-sm">📍 {gpsStatus}</p>
              </div>
            )}

            <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-green-700">
              <p className="text-green-400 font-bold mb-1">📋 Stone Review</p>
              <p className="text-gray-300 text-xs">Confirm each person and their relationships before matching to the database.</p>
              {stoneMatrix.stone_notes && <p className="text-gray-400 text-xs mt-1">{stoneMatrix.stone_notes}</p>}
              <SeverityBadge severity={stoneMatrix.severity} />
            </div>

            {stoneMatrix.people.map((person, pIndex) => (
              <div key={pIndex} className="bg-gray-800 rounded-lg p-4 mb-4 border border-gray-600">
                {/* Person header */}
                <p className="text-green-400 text-xs font-bold mb-2">Person {pIndex + 1}</p>

                <input
                  type="text"
                  value={person.correctedName}
                  onChange={e => updateCorrectedName(pIndex, e.target.value)}
                  className="w-full bg-white border-2 border-green-500 rounded p-2 text-gray-900 text-sm mb-2 outline-none focus:ring-2 focus:ring-green-400 placeholder-gray-500 font-medium"
                  placeholder="Full name"
                />

                {/* Pre-search indicator */}
                {person.preSearchResults && (
                  <p className="text-green-400 text-xs mb-2">
                    ✓ {person.preSearchResults.length} database match{person.preSearchResults.length !== 1 ? 'es' : ''} found
                  </p>
                )}

                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <p className="text-gray-200 text-xs mb-1 font-medium">Born</p>
                    <input
                      type="text"
                      value={person.geminiData.date_of_birth_verbatim || ''}
                      onChange={e => setStoneMatrix(prev => ({
                        ...prev,
                        people: prev.people.map((p, i) => i === pIndex
                          ? { ...p, geminiData: { ...p.geminiData, date_of_birth_verbatim: e.target.value } }
                          : p)
                      }))}
                      placeholder="Birth date"
                      className="w-full bg-white border-2 border-green-500 rounded p-2 text-gray-900 text-xs outline-none focus:ring-2 focus:ring-green-400 placeholder-gray-500 font-medium"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-200 text-xs mb-1 font-medium">Died</p>
                    <input
                      type="text"
                      value={person.geminiData.date_of_death_verbatim || ''}
                      onChange={e => setStoneMatrix(prev => ({
                        ...prev,
                        people: prev.people.map((p, i) => i === pIndex
                          ? { ...p, geminiData: { ...p.geminiData, date_of_death_verbatim: e.target.value } }
                          : p)
                      }))}
                      placeholder="Death date"
                      className="w-full bg-white border-2 border-green-500 rounded p-2 text-gray-900 text-xs outline-none focus:ring-2 focus:ring-green-400 placeholder-gray-500 font-medium"
                    />
                  </div>
                </div>

                {/* Maiden name */}
                <div className="mb-2">
                  <p className="text-gray-200 text-xs mb-1 font-medium">Maiden name (nee)</p>
                  <input
                    type="text"
                    value={person.geminiData.maiden_name || ''}
                    onChange={e => setStoneMatrix(prev => ({
                      ...prev,
                      people: prev.people.map((p, i) => i === pIndex
                        ? { ...p, geminiData: { ...p.geminiData, maiden_name: e.target.value } }
                        : p)
                    }))}
                    placeholder="Maiden name if shown on stone"
                    className="w-full bg-white border-2 border-green-500 rounded p-2 text-gray-900 text-xs outline-none focus:ring-2 focus:ring-green-400 placeholder-gray-500 font-medium"
                  />
                </div>

                {/* Kinship hints from Gemini */}
                {person.geminiData.kinship_hints?.length > 0 && (
                  <p className="text-yellow-400 text-xs mb-2">{person.geminiData.kinship_hints.join(', ')}</p>
                )}

                {/* Occupant toggle */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => updatePersonRole(pIndex, 'occupant')}
                    className={'flex-1 py-2 rounded text-sm font-bold ' + (person.role === 'occupant' ? 'bg-green-700 text-white' : 'bg-gray-700 text-gray-300')}
                  >
                    ⬛ Buried here
                  </button>
                  <button
                    onClick={() => updatePersonRole(pIndex, 'mentioned')}
                    className={'flex-1 py-2 rounded text-sm font-bold ' + (person.role === 'mentioned' ? 'bg-yellow-700 text-white' : 'bg-gray-700 text-gray-300')}
                  >
                    📝 Mentioned only
                  </button>
                </div>

                {/* Suggested relationships */}
                {person.relationships.map((rel, rIndex) => (
                  <div key={rIndex} className="bg-gray-700 rounded p-3 mb-2">
                    {/* Editable type dropdown */}
                    <div className="flex items-center gap-2 mb-1">
                      <select
                        value={rel.type}
                        onChange={e => setStoneMatrix(prev => ({
                          ...prev,
                          people: prev.people.map((p, i) => i !== pIndex ? p : {
                            ...p,
                            relationships: p.relationships.map((r, ri) => ri !== rIndex ? r : { ...r, type: e.target.value })
                          })
                        }))}
                        className="bg-gray-600 text-yellow-400 text-xs font-bold border border-gray-500 rounded px-1 py-0.5 outline-none"
                      >
                        <option value="spouse">Spouse of</option>
                        <option value="child">Child of</option>
                        <option value="parent">Parent of</option>
                        <option value="sibling">Sibling of</option>
                      </select>
                      {rel.rawNames.length > 0 && (
                        <span className="text-yellow-300 text-xs">{rel.rawNames.join(' & ')}</span>
                      )}
                    </div>
                    {rel.hint && <p className="text-gray-400 text-xs mb-2">"{rel.hint}"</p>}

                    {/* Link to another person on stone */}
                    {stoneMatrix.people.filter((_, i) => i !== pIndex).length > 0 && (
                      <>
                        <p className="text-gray-300 text-xs mb-1">Link to person on this stone:</p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {stoneMatrix.people.filter((_, i) => i !== pIndex).map((otherPerson, oIndex) => {
                            const actualIndex = oIndex >= pIndex ? oIndex + 1 : oIndex
                            return (
                              <button key={actualIndex}
                                onClick={() => { confirmRelationship(pIndex, rel, actualIndex); skipRelationship(pIndex, rIndex) }}
                                className="bg-green-700 hover:bg-green-600 text-white text-xs py-1 px-2 rounded">
                                {otherPerson.correctedName || 'Person ' + (actualIndex + 1)}
                              </button>
                            )
                          })}
                        </div>
                      </>
                    )}

                    {/* Search database for external person */}
                    <p className="text-gray-300 text-xs mb-1">Search database:</p>
                    <div className="flex gap-1 mb-1">
                      <input
                        type="text"
                        value={rel.relatedName ?? rel.rawNames[0] ?? ''}
                        onChange={e => updateRelField(pIndex, rIndex, 'relatedName', e.target.value)}
                        placeholder="Enter name to search…"
                        className="bg-white border-2 border-green-500 text-gray-900 text-xs rounded px-2 py-1 flex-1 outline-none placeholder-gray-500"
                      />
                      <button
                        onClick={() => searchRelatedPerson(pIndex, rIndex, rel.relatedName ?? rel.rawNames[0] ?? '')}
                        className="bg-blue-700 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded"
                      >
                        {rel.relSearching ? '…' : 'Search'}
                      </button>
                    </div>
                    {rel.relSearchResults && rel.relSearchResults.length === 0 && (
                      <p className="text-gray-400 text-xs mb-1">No results found.</p>
                    )}
                    {rel.relSearchResults && rel.relSearchResults.map((record, ri) => (
                      <button key={ri}
                        onClick={() => confirmRelationshipExternal(pIndex, rIndex, rel, record)}
                        className="block w-full text-left bg-gray-600 hover:bg-gray-500 text-xs py-2 px-2 rounded mb-1">
                        <span className="text-white font-medium">{record.full_name || [record.first_name, record.last_name].filter(Boolean).join(' ')}</span>
                        {record.maiden_name && <span className="text-gray-400"> · nee {record.maiden_name}</span>}
                        <span className="text-gray-300 block mt-0.5">
                          {record.date_of_birth_verbatim && `b. ${record.date_of_birth_verbatim}`}
                          {record.date_of_birth_verbatim && record.date_of_death_verbatim && ' · '}
                          {record.date_of_death_verbatim && `d. ${record.date_of_death_verbatim}`}
                          {record.is_occupant && <span className="ml-1 text-green-400">  ⬛ buried</span>}
                          {!record.is_occupant && record.is_mentioned && <span className="ml-1 text-yellow-300">  📝 mentioned</span>}
                        </span>
                      </button>
                    ))}
                    {(rel.relatedName || rel.rawNames[0]) && (
                      <button
                        onClick={() => confirmRelationshipNameOnly(pIndex, rIndex, rel)}
                        className="text-yellow-400 text-xs hover:text-yellow-300 block mb-1"
                      >
                        Confirm "{rel.relatedName || rel.rawNames[0]}" without DB match
                      </button>
                    )}

                    <button onClick={() => skipRelationship(pIndex, rIndex)}
                      className="text-gray-400 text-xs hover:text-gray-200">
                      Skip this relationship
                    </button>
                  </div>
                ))}

                {/* Add a relationship Gemini missed */}
                <button
                  onClick={() => setStoneMatrix(prev => ({
                    ...prev,
                    people: prev.people.map((p, i) => i !== pIndex ? p : {
                      ...p,
                      relationships: [...p.relationships, { type: 'spouse', rawNames: [], hint: '', implicit: false }]
                    })
                  }))}
                  className="text-green-400 text-xs mb-3 hover:text-green-300"
                >
                  + Add relationship
                </button>

                {/* Confirmed relationships */}
                {person.confirmedRelationships.length > 0 && (
                  <div className="mt-2">
                    {person.confirmedRelationships.map((rel, i) => (
                      <div key={i} className="flex items-center justify-between py-0.5">
                        <p className="text-green-400 text-xs">
                          ✓ {REL_LABEL[rel.type] || rel.type} {rel.objectName}
                        </p>
                        <button
                          onClick={() => removeConfirmedRelationship(pIndex, i)}
                          className="text-gray-500 text-xs hover:text-red-400 ml-3 shrink-0"
                        >
                          ↩ undo
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <button onClick={proceedToMatch}
              className="w-full bg-green-700 hover:bg-green-600 text-white font-bold py-4 rounded-lg mb-3">
              Continue → Match to Database
            </button>
            <button onClick={clearAndReset}
              className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg text-sm">
              Start Over
            </button>
          </>
        )}

        {/* ── PHASE: MATCH ── */}
        {photoPhase === 'match' && stoneMatrix && (
          <>
            {saving && (
              <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                <div className="bg-gray-800 rounded-lg p-6 text-center mx-4">
                  <p className="text-white text-xl mb-2">⏳ Saving...</p>
                  <p className="text-gray-400 text-sm">Please wait, do not tap again</p>
                  {gpsStatus && <p className="text-blue-400 text-xs mt-2">📍 {gpsStatus}</p>}
                </div>
              </div>
            )}

            {gpsStatus && (
              <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-blue-700">
                <p className="text-blue-400 text-sm">📍 {gpsStatus}</p>
              </div>
            )}

            <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-green-700">
              <p className="text-green-400 font-bold">
                Match {matchingIndex + 1} of {stoneMatrix.people.length}
              </p>
              <p className="text-gray-300 text-xs">
                {stoneMatrix.people.filter(p => p.matchStatus === 'matched').length} matched •{' '}
                {stoneMatrix.people.filter(p => p.matchStatus === 'new').length > 0 && (
                  <>{stoneMatrix.people.filter(p => p.matchStatus === 'new').length} new record •{' '}</>
                )}
                {stoneMatrix.people.filter(p => p.matchStatus === 'skipped').length} skipped •{' '}
                {stoneMatrix.people.filter(p => p.matchStatus === 'pending').length} pending
              </p>
            </div>

            {/* Current person to match */}
            {(() => {
              const person = stoneMatrix.people[matchingIndex]
              if (!person) return null
              return (
                <div className="bg-gray-800 rounded-lg p-4 mb-4 border border-gray-600">
                  <p className="text-green-400 text-xs font-bold mb-1">
                    {person.role === 'occupant' ? '⬛ Occupant' : '📝 Mentioned'}
                  </p>
                  <p className="text-white font-bold text-lg">{person.correctedName}</p>
                  {person.geminiData.date_of_birth_verbatim && (
                    <p className="text-gray-300 text-sm">b. {person.geminiData.date_of_birth_verbatim}</p>
                  )}
                  {person.geminiData.date_of_death_verbatim && (
                    <p className="text-gray-300 text-sm">d. {person.geminiData.date_of_death_verbatim}</p>
                  )}

                  {person.matchStatus === 'matched' && (
                    <div className="mt-2 bg-green-900 rounded p-2">
                      <p className="text-green-400 text-sm">✓ Matched: {person.matchedRecord.full_name}</p>
                      <button onClick={() => {
                        setStoneMatrix(prev => ({
                          ...prev,
                          people: prev.people.map((p, i) => i === matchingIndex ? { ...p, matchedRecord: null, matchStatus: 'pending' } : p)
                        }))
                        setMatchSearchResults([])
                      }} className="text-gray-400 text-xs hover:text-gray-200 mt-1">Change match</button>
                    </div>
                  )}

                  {person.matchStatus !== 'matched' && (
                    <>
                      <div className="flex gap-2 mt-3 mb-3">
                        <input
                          type="text"
                          value={matchSearchQuery}
                          onChange={e => setMatchSearchQuery(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleMatchSearch(matchSearchQuery)}
                          placeholder="Search database..."
                          className="flex-1 bg-white border-2 border-green-500 rounded p-2 text-gray-900 text-sm outline-none focus:ring-2 focus:ring-green-400 font-medium placeholder-gray-500"
                        />
                        <button onClick={() => handleMatchSearch(matchSearchQuery)} disabled={matchSearching}
                          className="bg-green-700 hover:bg-green-600 disabled:bg-gray-600 text-white font-bold px-3 rounded text-sm min-w-[60px]">
                          {matchSearching ? '⏳' : 'Search'}
                        </button>
                      </div>

                      {matchSearching && (
                        <div className="bg-gray-700 rounded p-3 mb-3 text-center">
                          <p className="text-green-400 text-sm">Searching...</p>
                        </div>
                      )}

                      {/* Only show results and skip/save actions when search is complete */}
                      {!matchSearching && matchSearchResults.map(record => (
                        <div key={record.deceased_id}
                          className={'p-3 rounded-lg mb-2 cursor-pointer ' + (record.is_occupant ? 'bg-gray-700 border border-yellow-600' : 'bg-gray-700')}
                          onClick={() => selectMatch(record)}>
                          <p className={'font-bold text-sm ' + (record.is_occupant ? 'text-yellow-400' : 'text-white')}>
                            {record.full_name}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap mt-0.5">
                            <span className="text-gray-300 text-xs">
                              {record.date_of_birth_verbatim && 'b. ' + record.date_of_birth_verbatim}
                              {record.date_of_birth_verbatim && record.date_of_death_verbatim && ' · '}
                              {record.date_of_death_verbatim && 'd. ' + record.date_of_death_verbatim}
                              {record.maiden_name && ' · nee ' + record.maiden_name}
                            </span>
                            {record.is_occupant && <span className="text-green-400 text-xs">⬛ Buried</span>}
                            {!record.is_occupant && record.is_mentioned && <span className="text-yellow-300 text-xs">📝 Mentioned</span>}
                          </div>
                        </div>
                      ))}

                      {!matchSearching && matchSearchAttempted && matchSearchResults.length === 0 && (
                        <p className="text-gray-200 text-sm text-center py-2">No matches found — try a shorter name or last name only</p>
                      )}
                    </>
                  )}
                </div>
              )
            })()}

            <div className="flex gap-2 mb-4">
  {stoneMatrix.people[matchingIndex]?.matchStatus === 'matched' ? (
    <button onClick={nextPerson} disabled={saving}
      className="flex-1 bg-green-700 hover:bg-green-600 disabled:bg-gray-600 text-white font-bold py-3 rounded-lg">
      {saving ? '⏳ Saving...' : matchingIndex + 1 < stoneMatrix.people.length ? 'Next Person →' : '💾 Save Stone'}
    </button>
  ) : matchSearching ? (
    <p className="text-green-400 text-sm py-3">⏳ Searching...</p>
  ) : matchSearchAttempted ? (
    <>
      <button onClick={() => { skipMatch(); nextPerson() }} disabled={saving}
        className="flex-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-3 rounded-lg text-sm">
        Skip — no match
      </button>
      <button onClick={() => { markAsNewRecord(); nextPerson() }} disabled={saving}
        className="flex-1 bg-amber-700 hover:bg-amber-600 disabled:bg-gray-600 text-white font-bold py-3 rounded-lg text-sm">
        + New record
      </button>
    </>
  ) : (
    <p className="text-gray-400 text-sm py-3">Search above to find a match</p>
  )}
</div>

            {/* Field notes */}
            <div className="mb-4">
              <button onClick={() => setShowNotes(!showNotes)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg py-3 px-4 text-left text-gray-300 text-sm flex items-center justify-between">
                <span>📝 Add Field Notes (optional)</span>
                <span>{showNotes ? '▲' : '▼'}</span>
              </button>
              {showNotes && (
                <div className="bg-gray-800 border border-gray-600 border-t-0 rounded-b-lg p-4">
                  <textarea value={volunteerNotes} onChange={e => setVolunteerNotes(e.target.value)}
                    placeholder="Observations about this stone..."
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 text-sm outline-none focus:ring-2 focus:ring-green-500 mb-3"
                    rows={3} />
                  <p className="text-gray-400 text-xs mb-2">Flag for follow-up:</p>
                  {['Needs re-photographing', 'Check back or other side', 'Stone needs cleaning',
                    'Person not in database — needs new record', 'Other issue'].map(flag => (
                    <label key={flag} className="flex items-center gap-2 mb-2 cursor-pointer">
                      <input type="checkbox" checked={selectedFlags.includes(flag)}
                        onChange={e => {
                          if (e.target.checked) setSelectedFlags(prev => [...prev, flag])
                          else setSelectedFlags(prev => prev.filter(f => f !== flag))
                        }} className="w-4 h-4" />
                      <span className="text-gray-300 text-sm">{flag}</span>
                    </label>
                  ))}
                  {(volunteerNotes || selectedFlags.length > 0) && (
                    <button onClick={saveNotes} disabled={savingNotes}
                      className="w-full bg-green-700 hover:bg-green-600 disabled:bg-gray-600 text-white font-bold py-2 rounded-lg text-sm mt-2">
                      {savingNotes ? 'Saving...' : 'Save Notes'}
                    </button>
                  )}
                </div>
              )}
            </div>

            <button onClick={clearAndReset} className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg text-sm">
              Start Over
            </button>
          </>
        )}

        {/* ── PHASE: DONE ── */}
        {photoPhase === 'done' && (
          <div className="text-center py-12">
            <p className="text-green-400 text-4xl mb-4">✓</p>
            <p className="text-white text-xl font-bold mb-2">Stone Saved!</p>
            <p className="text-gray-300 text-sm mb-8">
              {stoneMatrix?.people?.filter(p => p.matchStatus === 'matched').length || 0} matched •{' '}
              {stoneMatrix?.people?.filter(p => p.matchStatus === 'skipped').length || 0} skipped
            </p>
            <button onClick={clearAndReset}
              className="w-full bg-green-700 hover:bg-green-600 text-white font-bold py-4 rounded-lg text-lg">
              📷 Photograph Another Stone
            </button>
          </div>
        )}

      </div>
    </div>
  )
}
